import { ListStore } from '@list-store';
import { uniq } from 'lodash';
import {
    Attribute,
    ManageUnitsResource,
    ManageUnitsResponse,
    ManageUnitsUnitGroupEnum,
    ManageUnitsUnitGroupEnumType,
    ManageUnitsUnitView,
    NudgeObject,
    UnitFiltersForm,
    UnitStateGroupEnum,
} from '@mark43/cad-api';
import { transaction } from 'mobx';
import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { numberComparator } from '../common/sorting/comparators';
import { createStreamListener, useStream } from '../socket/use-stream';
import { useSelectionState } from './common/use-selection-state';

type ManageUnitsStore = {
    store: ListStore<ManageUnitsUnitView>;
    manageUnitsEventResponse?: ManageUnitsResponse;
    manageUnitsUnitsResponse?: ManageUnitsResponse;
    isEventQueryLoading: boolean;
    isUnitsQueryLoading: boolean;
    isUnitsQueryError: boolean;
    areUnitsFetchedAfterMount: boolean;
    selectedUnitIds: number[];
    releasedUnitIds: number[];
    toggleUnitSelection: (unit: number) => void;
};

const nonSelectedGroups: ManageUnitsUnitGroupEnumType[] = ['AVAILABLE', 'UNAVAILABLE'];

export function useIntegratedManageUnitsStore({
    eventId,
    defaultFilters,
    selectedFilters,
    unitTypeAttributes,
}: {
    eventId: number;
    defaultFilters: UnitFiltersForm;
    selectedFilters: UnitFiltersForm;
    unitTypeAttributes: Attribute[];
}): ManageUnitsStore {
    const apiClient = useApiClient();

    const selectionState = useSelectionState();
    const selectedUnitIds = selectionState.selectedItemIds;

    const groupingDirective = useMemo(() => {
        return {
            getGroupValue: (row: ManageUnitsUnitView): ManageUnitsUnitGroupEnumType =>
                nonSelectedGroups.includes(row.manageUnitGroup)
                    ? selectedUnitIds.includes(row.id)
                        ? 'TO_DISPATCH'
                        : row.manageUnitGroup
                    : row.manageUnitGroup,
            comparator: (
                left: ManageUnitsUnitGroupEnumType,
                right: ManageUnitsUnitGroupEnumType
            ) => {
                return numberComparator(
                    ManageUnitsUnitGroupEnum[left].index,
                    ManageUnitsUnitGroupEnum[right].index
                );
            },
        };
    }, [selectedUnitIds]);

    const shouldRefetchUnits =
        !!selectedFilters &&
        unitTypeAttributes.length > 0 &&
        (selectedFilters.emsUnitTypeIds.length > 0 ||
            selectedFilters.policeUnitTypeIds.length > 0 ||
            selectedFilters.fireUnitTypeIds.length > 0);
    const store = React.useState(
        () =>
            new ListStore<ManageUnitsUnitView>({
                groupingDirective,
            })
    )[0];

    useEffect(() => {
        store.setGroupingDirective(groupingDirective);
    }, [store, groupingDirective]);

    const eventQuery = useQuery({
        queryKey: ['manage-units-event', eventId, defaultFilters] as const,
        queryFn({ queryKey }) {
            return apiClient.request<ManageUnitsResource.GetByEvent>({
                method: 'POST',
                url: 'manage_units/event/',
                data: {
                    ...queryKey[2],
                    eventId: queryKey[1],
                    selectedUnitIds: [],
                    shouldIncludeResponsePlan: false,
                },
            });
        },
    });

    const { refetch: unitsRefetch, ...unitsQuery } = useQuery({
        queryKey: ['manage-units-units', eventId, selectedFilters],
        queryFn() {
            return apiClient.request<ManageUnitsResource.GetByEvent>({
                method: 'POST',
                url: 'manage_units/event/',
                data: {
                    ...selectedFilters,
                    selectedUnitIds,
                    shouldIncludeResponsePlan: true,
                    eventId,
                },
            });
        },
        enabled: shouldRefetchUnits,
        onSuccess(response) {
            if (response.data.success) {
                if (response.data.data?.units) {
                    store.setItems(response.data.data.units);
                }
                const initiallySelectedUnits = response.data.data?.initiallySelectedUnits;
                if (initiallySelectedUnits) {
                    store.upsertItems(initiallySelectedUnits);
                    selectionState.resetInitiallySelectedItems(
                        initiallySelectedUnits.map((unit) => unit.id)
                    );
                }
            }
        },
        notifyOnChangeProps: ['data', 'isInitialLoading'],
    });

    const fetchUnitsByIds = React.useMemo(() => {
        return (unitIds: number[]) => {
            return apiClient
                .request<ManageUnitsResource.GetByUnits>({
                    method: 'POST',
                    url: 'manage_units/units/',
                    data: {
                        ...selectedFilters,
                        selectedUnitIds,
                        eventId,
                        unitIds,
                    },
                })
                .then((response) => {
                    if (response.data.data) {
                        const {
                            units,
                            initiallySelectedUnits,
                            responsePlanV2,
                        } = response.data.data;

                        transaction(() => {
                            const unitIdsInResponse = units.map((unit) => unit.id);
                            const unitIdsWeAskedForThatDidntComeBackInResponse = unitIds.filter(
                                (unitId) => !unitIdsInResponse.includes(unitId)
                            );
                            const recommendedUnitIds = responsePlanV2?.unitIdsAlreadyInResponsePlan;
                            store.removeItems(unitIdsWeAskedForThatDidntComeBackInResponse);
                            store.upsertItems(units);

                            if (!initiallySelectedUnits.length && responsePlanV2) {
                                selectionState.resetInitiallySelectedItems([
                                    ...(recommendedUnitIds || []),
                                ]);
                            }

                            if (initiallySelectedUnits.length) {
                                const initiallySelectedUnitIds = initiallySelectedUnits.map(
                                    (unit) => unit.id
                                );

                                const unitIdsNeedToBeReset = [
                                    ...initiallySelectedUnitIds,
                                    ...(recommendedUnitIds || []),
                                ];

                                selectionState.resetInitiallySelectedItems(unitIdsNeedToBeReset);

                                for (let i = 0; i < initiallySelectedUnits.length; i++) {
                                    if (
                                        !store.data.list.find(
                                            (unit) => unit.id === initiallySelectedUnits[i].id
                                        )
                                    ) {
                                        store.upsertItems([initiallySelectedUnits[i]]);
                                        selectionState.resetInitiallySelectedItems(
                                            unitIdsNeedToBeReset
                                        );
                                    }
                                }
                            }
                        });
                    }
                });
        };
    }, [apiClient, store, eventId, selectedUnitIds, selectedFilters, selectionState]);

    const getUnitIdsToEvictFromUnitStatusNudges = useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    // nudge is for a unit that isn't in the store, no need to evict
                    if (unitStatusNudge.u && !store.hasItem(unitStatusNudge.u)) {
                        return false;
                    }
                    // unit is logged off
                    if (unitStatusNudge.usg === UnitStateGroupEnum.OFF_DUTY.value) {
                        return true;
                    }
                    // don't evict a selected unit (unless it's logged off)
                    if (unitStatusNudge.u && selectedUnitIds.includes(unitStatusNudge.u)) {
                        return false;
                    }
                    // unit is not in d-area we're filtering for
                    if (selectedFilters.dispatchAreaIds.length > 0 && unitStatusNudge.d) {
                        if (unitStatusNudge.ed) {
                            if (
                                !selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d) &&
                                !selectedFilters.dispatchAreaIds.includes(unitStatusNudge.ed)
                            ) {
                                return true;
                            }
                        } else {
                            if (!selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d)) {
                                return true;
                            }
                        }
                    }
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        };
    }, [store, selectedFilters, selectedUnitIds]);

    const getUnitIdsToUpdateFromUnitStatusNudges = React.useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    return unitStatusNudge.usg !== UnitStateGroupEnum.OFF_DUTY.value;
                })
                .filter((unitStatusNudge) => {
                    if (unitStatusNudge.u && selectedUnitIds.includes(unitStatusNudge.u)) {
                        return true;
                    }
                    if (selectedFilters.dispatchAreaIds.length > 0 && unitStatusNudge.d) {
                        if (unitStatusNudge.ed) {
                            return (
                                selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d) ||
                                selectedFilters.dispatchAreaIds.includes(unitStatusNudge.ed)
                            );
                        } else {
                            return selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d);
                        }
                    } else {
                        return true;
                    }
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        };
    }, [selectedFilters.dispatchAreaIds, selectedUnitIds]);

    const getUnitIdsInStoreFromUnitNudges = React.useMemo(() => {
        return (unitNudges: NudgeObject[] = []): number[] => {
            return unitNudges
                .map((unitNudge) => {
                    return unitNudge.id;
                })
                .filter((unitId) => !!unitId && store.hasItem(unitId));
        };
    }, [store]);

    const getUnitIdsInStoreFromNonUnitNudges = React.useMemo(() => {
        return (nonUnitNudges: NudgeObject[] = []): number[] => {
            return nonUnitNudges
                .map((nonUnitNudge) => {
                    return nonUnitNudge.u;
                })
                .filter((unitId) => !!unitId && store.hasItem(unitId)) as number[];
        };
    }, [store]);

    const listener = useMemo(() => {
        return createStreamListener({
            streamNames: ['nudge'],
            onMessage: (message) => {
                const unitIdsToEvict = getUnitIdsToEvictFromUnitStatusNudges(
                    message.payload.map['USS']
                );
                if (unitIdsToEvict.length > 0) {
                    store.removeItems(unitIdsToEvict);
                }
                const unitIdsToUpdate = [
                    ...getUnitIdsToUpdateFromUnitStatusNudges(message.payload.map['USS']),
                    ...getUnitIdsInStoreFromUnitNudges(message.payload.map['U']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['UUL']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['ULI']),
                ];
                if (unitIdsToUpdate.length > 0) {
                    fetchUnitsByIds(uniq(unitIdsToUpdate));
                }
            },
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (
                    socketStatusChangeEvent.status === 'CONNECTED' &&
                    socketStatusChangeEvent.timesConnected > 1
                ) {
                    unitsRefetch();
                }
            },
        });
    }, [
        getUnitIdsToUpdateFromUnitStatusNudges,
        getUnitIdsInStoreFromUnitNudges,
        getUnitIdsInStoreFromNonUnitNudges,
        getUnitIdsToEvictFromUnitStatusNudges,
        fetchUnitsByIds,
        store,
        unitsRefetch,
    ]);

    useStream(listener);

    return {
        store,
        manageUnitsEventResponse: eventQuery.data?.data.data,
        manageUnitsUnitsResponse: unitsQuery.data?.data.data,
        isEventQueryLoading: eventQuery.isInitialLoading,
        isUnitsQueryLoading: unitsQuery.isInitialLoading,
        isUnitsQueryError: unitsQuery.isError,
        selectedUnitIds,
        toggleUnitSelection: selectionState.toggleItemSelection,
        releasedUnitIds: selectionState.unselectedItemIds,
        areUnitsFetchedAfterMount: unitsQuery.isFetchedAfterMount,
    };
}
